import {
  createErrorCollector,
  createResourceTimingReader,
  createTimingCollector,
} from '@erebus/telemetry';
import { getRuntimeConfig } from '@erebus/runtime-config';
import { isCookieAuthMode } from './authMode';

/** Where the collector lives on the Go backend. */
const COLLECTOR_PATH = '/telemetry/timing';

/** Where failed calls made before sign-in go. Needs no login. */
const ERROR_COLLECTOR_PATH = '/telemetry/errors';

/**
 * Wire up client timing telemetry.
 *
 * Connect authenticates with a bearer token, which is why the batch goes out
 * through `fetch` with `keepalive` rather than `navigator.sendBeacon`: a beacon
 * cannot set an Authorization header. `keepalive` can, and still survives the
 * page being closed, which is when the last batch would otherwise be lost.
 *
 * In cookie mode there is no token: the request carries the session cookie
 * instead (`credentials: 'include'`), and the collector runs while the user is
 * signed in.
 *
 * Calls that fail while the user is NOT signed in go to a second collector,
 * which posts to `/telemetry/errors`. That request carries no cookie and no
 * Authorization header: the caller has no identity yet, and the endpoint takes
 * none.
 *
 * @param {Object} deps
 * @param {Object} deps.authStore - Supplies the bearer token, or the signed-in state
 * @returns {{ record: Function, recordError: Function, stop: Function }} the running collectors
 */
export function startTimingTelemetry({ authStore }) {
  const reader = createResourceTimingReader();
  const config = getRuntimeConfig();

  /**
   * Read at flush time rather than once at boot, because the user logs in
   * after the app has already started making calls.
   */
  const signedIn = () =>
    isCookieAuthMode() ? Boolean(authStore.isAuthenticated) : Boolean(authStore.token);

  /** How the batch proves who sent it: the session cookie, or the token as a header. */
  const credentials = () =>
    isCookieAuthMode()
      ? { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      : {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authStore.token}`,
          },
        };

  const send = async (records) => {
    const response = await fetch(`${config.apiBaseUrl}${COLLECTOR_PATH}`, {
      method: 'POST',
      // Survives the page being closed, unlike a plain fetch, and unlike
      // sendBeacon it can carry the Authorization header this app needs.
      keepalive: true,
      ...credentials(),
      body: JSON.stringify({ records }),
    });

    // Throwing is how the collector learns a flush failed; it retries once and
    // then drops the batch rather than holding the connection.
    if (!response.ok) {
      throw new Error(`collector returned ${response.status}`);
    }
  };

  const collector = createTimingCollector({
    send,
    isEnabled: signedIn,
    samplePercent: config.telemetrySamplePercent,
    slowCallMs: SLOW_CALL_MS,
    resourceTimings: (url) => reader.take(url),
  });

  const sendErrors = async (records) => {
    const response = await fetch(`${config.apiBaseUrl}${ERROR_COLLECTOR_PATH}`, {
      method: 'POST',
      keepalive: true,
      // No cookie and no token, on purpose. Without this, a cross-origin fetch
      // already omits credentials; it is stated so nobody adds them later.
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records }),
    });

    // A 404 (flag off) or 429 (rate limited) is a failed flush. The status rides
    // on the error so the collector can drop a 4xx without retrying it.
    if (!response.ok) {
      throw Object.assign(new Error(`error collector returned ${response.status}`), {
        status: response.status,
      });
    }
  };

  const errorCollector = createErrorCollector({
    send: sendErrors,
    isEnabled: () => !signedIn(),
  });

  // The last batch of a session is the interesting one - it holds whatever the
  // user was doing when they gave up and closed the tab. 'hidden' is the last
  // event a browser reliably delivers; 'beforeunload' is not.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      void collector.flush();
      void errorCollector.flush();
    }
  };

  const listening = typeof document !== 'undefined';
  if (listening) {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return {
    record: collector.record,
    recordError: errorCollector.record,
    stop: () => {
      collector.stop();
      errorCollector.stop();
      reader.stop();
      // Removed here too, not just the collector and reader. Every re-install
      // of the axios interceptors starts a fresh collector, so a listener left
      // attached would accumulate one dead handler per install.
      if (listening) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    },
  };
}

/**
 * Threshold past which a call is always reported, matching SLOW_API_CALL_MS in
 * the axios layer and SLOW_QUERY_THRESHOLD_MS in the legacy Java app. Kept in
 * step so "slow" means one thing everywhere.
 */
export const SLOW_CALL_MS = 1000;
